import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { securityAPI } from '../../services/api';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';

interface ScanStatus {
  scan_id: number;
  asset_id: number;
  scan_type: string;
  status: string;
  progress: number;
  total_pages: number;
  scanned_pages: number;
  issues_found: number;
  started_at: string;
  completed_at: string | null;
  duration: number | null;
  error_message: string | null;
}

interface RecentScan {
  id: number;
  status: string;
  scan_type: string;
  issues_found: number;
  started_at: string;
  completed_at: string;
}

type ScanType = 'quick' | 'standard' | 'full';

const SCAN_TYPES: { [key in ScanType]: { label: string; description: string; icon: string; modules: string[] } } = {
  quick: {
    label: 'Quick Scan',
    description: 'Essential security checks (SSL, Headers, Clickjacking)',
    icon: 'bi-lightning',
    modules: ['ssl', 'headers', 'clickjacking']
  },
  standard: {
    label: 'Standard Scan',
    description: 'Common vulnerabilities plus exposed files (.git, .env, backups) and directory listing',
    icon: 'bi-shield-check',
    modules: ['ssl', 'headers', 'cookies', 'mixed_content', 'clickjacking', 'exposure']
  },
  full: {
    label: 'Full Scan',
    description: 'Complete audit: SSL, headers, cookies, CORS, exposed files, directory listing, server fingerprint, mixed content and forms',
    icon: 'bi-shield-fill-check',
    modules: ['ssl', 'headers', 'cookies', 'mixed_content', 'exposure', 'cors', 'clickjacking', 'server_fingerprint', 'forms_deep']
  }
};

const SecurityScanningPage: React.FC = () => {
  useDocumentTitle('Security Scanning');
  const navigate = useNavigate();
  const { currentAsset } = useAuth();
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanType, setScanType] = useState<ScanType>('standard');
  const [scanStatus, setScanStatus] = useState<ScanStatus | null>(null);
  const [recentScans, setRecentScans] = useState<RecentScan[]>([]);
  const [securityScore, setSecurityScore] = useState<number>(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const scanStartTimeRef = useRef<Date | null>(null);

  useEffect(() => {
    if (currentAsset) {
      loadSecurityData();
    }
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [currentAsset]);

  const loadSecurityData = async () => {
    if (!currentAsset) return;

    setLoading(true);
    try {
      const response = await securityAPI.getOverview(currentAsset.id);
      if (response.data.status === 'success') {
        const data = response.data.data;
        setRecentScans(data.recent_scans || []);
        setSecurityScore(data.security_score || 0);

        // Check if there's an active scan
        const activeScan = data.recent_scans?.find((s: RecentScan) => s.status === 'running');
        if (activeScan) {
          setScanning(true);
          scanStartTimeRef.current = new Date(activeScan.started_at);
          startPolling(activeScan.id);
          startTimer();
        }
      }
    } catch (error: any) {
      if (error.response?.status !== 401) {
        console.error('Failed to load security data:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const startScan = async () => {
    if (!currentAsset) return;

    // Navigate to the animated progress page with scan configuration
    navigate('/security/scan-progress', {
      state: {
        modules: SCAN_TYPES[scanType].modules,
        scanType: scanType
      }
    });
  };

  const startPolling = (scanId: number) => {
    pollIntervalRef.current = setInterval(async () => {
      try {
        const response = await securityAPI.getScanStatus(scanId);
        if (response.data.status === 'success') {
          const status = response.data.data;
          setScanStatus(status);

          if (status.status === 'completed' || status.status === 'failed') {
            stopPolling();
            stopTimer();
            setScanning(false);
            loadSecurityData();

            if (status.status === 'completed') {
              toast.success('Security scan completed!');
            } else {
              toast.error('Scan failed: ' + (status.error_message || 'Unknown error'));
            }
          }
        }
      } catch (error) {
        console.error('Error polling scan status:', error);
      }
    }, 3000);
  };

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      if (scanStartTimeRef.current) {
        setElapsedTime(Math.floor((Date.now() - scanStartTimeRef.current.getTime()) / 1000));
      }
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const downloadReport = async () => {
    if (!currentAsset) return;

    try {
      toast.loading('Generating PDF report...');
      const response = await securityAPI.generatePdfReport(currentAsset.id);

      // Create blob from response data (PDF binary)
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `security-report-${currentAsset.id}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.dismiss();
      toast.success('PDF Report downloaded successfully');
    } catch (error: any) {
      toast.dismiss();
      console.error('Failed to generate report:', error);
      toast.error('Failed to generate PDF report');
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-success';
      case 'running': return 'bg-primary';
      case 'failed': return 'bg-danger';
      case 'pending': return 'bg-warning';
      default: return 'bg-secondary';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    if (score >= 40) return 'orange';
    return 'danger';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container-fluid">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="h3 mb-1">Security Scanning</h1>
            <p className="text-muted mb-0">Run vulnerability scans on your assets</p>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-primary" onClick={downloadReport} disabled={scanning}>
              <i className="bi bi-download me-2"></i>Download Report
            </button>
            <Link to="/security/results" className="btn btn-outline-success">
              <i className="bi bi-file-earmark-text me-2"></i>View Results
            </Link>
          </div>
        </div>

        {/* Active Scan Progress */}
        {scanning && (
          <div className="card border-0 shadow-sm mb-4 border-start border-primary border-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center">
                  <div className="spinner-border spinner-border-sm text-primary me-3" role="status"></div>
                  <h5 className="mb-0">Security Scan in Progress</h5>
                </div>
                <span className="badge bg-primary fs-6">
                  <i className="bi bi-clock me-1"></i>{formatTime(elapsedTime)}
                </span>
              </div>

              <div className="mb-3">
                <div className="d-flex justify-content-between mb-2">
                  <span>
                    Scanning: <strong className="text-primary">{SCAN_TYPES[scanType].label}</strong>
                  </span>
                  <span className="fw-semibold">
                    {scanStatus?.progress || 0}%
                  </span>
                </div>
                <div className="progress" style={{ height: '12px' }}>
                  <div
                    className="progress-bar bg-primary progress-bar-striped progress-bar-animated"
                    style={{ width: `${scanStatus?.progress || 0}%` }}
                  ></div>
                </div>
              </div>

              <div className="row g-3">
                <div className="col-md-4">
                  <div className="bg-light rounded p-3 text-center">
                    <div className="text-muted small">Modules Scanned</div>
                    <div className="fw-bold fs-4 text-primary">
                      {scanStatus?.scanned_pages || 0}/{scanStatus?.total_pages || SCAN_TYPES[scanType].modules.length}
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="bg-light rounded p-3 text-center">
                    <div className="text-muted small">Issues Found</div>
                    <div className={`fw-bold fs-4 ${(scanStatus?.issues_found || 0) > 0 ? 'text-danger' : 'text-success'}`}>
                      {scanStatus?.issues_found || 0}
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="bg-light rounded p-3 text-center">
                    <div className="text-muted small">Status</div>
                    <div className="fw-bold fs-5">
                      <span className={`badge ${getStatusBadgeClass(scanStatus?.status || 'running')}`}>
                        {scanStatus?.status || 'Running'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Scan Configuration */}
        {!scanning && (
          <div className="row g-4 mb-4">
            <div className="col-lg-8">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom py-3">
                  <h5 className="mb-0">Start New Scan</h5>
                </div>
                <div className="card-body">
                  <div className="mb-4">
                    <label className="form-label fw-semibold">Select Scan Type</label>
                    <div className="row g-3">
                      {(Object.keys(SCAN_TYPES) as ScanType[]).map(type => (
                        <div key={type} className="col-md-4">
                          <div
                            className={`card h-100 ${scanType === type ? 'border-primary bg-primary bg-opacity-10' : 'border'}`}
                            onClick={() => setScanType(type)}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="card-body text-center py-4">
                              <i className={`bi ${SCAN_TYPES[type].icon} fs-2 ${scanType === type ? 'text-primary' : 'text-muted'} mb-3 d-block`}></i>
                              <h6 className="fw-semibold mb-2">{SCAN_TYPES[type].label}</h6>
                              <small className="text-muted d-block mb-2">{SCAN_TYPES[type].description}</small>
                              <span className="badge bg-secondary">{SCAN_TYPES[type].modules.length} modules</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mb-4">
                    <h6 className="fw-semibold mb-3">Modules Included:</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {SCAN_TYPES[scanType].modules.map(module => (
                        <span key={module} className="badge bg-light text-dark border">
                          <i className="bi bi-check-circle text-success me-1"></i>
                          {module.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      ))}
                    </div>
                  </div>

                  <button
                    className="btn btn-primary btn-lg w-100"
                    onClick={startScan}
                    disabled={scanning}
                  >
                    <i className="bi bi-shield-check me-2"></i>
                    Start {SCAN_TYPES[scanType].label}
                  </button>
                </div>
              </div>
            </div>

            <div className="col-lg-4">
              {/* Security Score Card */}
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-body text-center py-4">
                  <div className={`display-4 fw-bold text-${getScoreColor(securityScore)}`}>
                    {Math.round(securityScore)}
                  </div>
                  <div className="text-muted mb-3">Security Score</div>
                  <div className="progress mb-2" style={{ height: '8px' }}>
                    <div
                      className={`progress-bar bg-${getScoreColor(securityScore)}`}
                      style={{ width: `${securityScore}%` }}
                    ></div>
                  </div>
                  <small className="text-muted">Based on last scan results</small>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom py-3">
                  <h6 className="mb-0">Quick Actions</h6>
                </div>
                <div className="list-group list-group-flush">
                  <Link to="/security/results" className="list-group-item list-group-item-action">
                    <i className="bi bi-file-earmark-bar-graph text-primary me-2"></i>
                    View Full Report
                  </Link>
                  <Link to="/security/vulnerabilities" className="list-group-item list-group-item-action">
                    <i className="bi bi-exclamation-triangle text-warning me-2"></i>
                    View All Issues
                  </Link>
                  <button className="list-group-item list-group-item-action" onClick={downloadReport}>
                    <i className="bi bi-download text-success me-2"></i>
                    Download PDF Report
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Scan History */}
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Scan History</h5>
            <button className="btn btn-outline-primary btn-sm" onClick={loadSecurityData}>
              <i className="bi bi-arrow-clockwise me-1"></i>Refresh
            </button>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Scan ID</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Issues</th>
                    <th>Started</th>
                    <th>Completed</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recentScans.map((scan) => (
                    <tr key={scan.id}>
                      <td className="fw-medium">#{scan.id}</td>
                      <td>
                        <span className="badge bg-secondary text-capitalize">{scan.scan_type}</span>
                      </td>
                      <td>
                        <span className={`badge ${getStatusBadgeClass(scan.status)}`}>
                          {scan.status}
                        </span>
                      </td>
                      <td>
                        {scan.issues_found > 0 ? (
                          <span className="badge bg-warning text-dark">{scan.issues_found}</span>
                        ) : (
                          <span className="text-success"><i className="bi bi-check-circle"></i> 0</span>
                        )}
                      </td>
                      <td className="text-muted small">
                        {new Date(scan.started_at).toLocaleString()}
                      </td>
                      <td className="text-muted small">
                        {scan.completed_at ? new Date(scan.completed_at).toLocaleString() : '-'}
                      </td>
                      <td className="text-end">
                        <div className="btn-group btn-group-sm">
                          <Link to="/security/results" className="btn btn-outline-primary" title="View Results">
                            <i className="bi bi-eye"></i>
                          </Link>
                          <button className="btn btn-outline-success" onClick={downloadReport} title="Download Report">
                            <i className="bi bi-download"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {recentScans.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-5">
                        <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                        No scan history. Start your first security scan above.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SecurityScanningPage;
